import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fastapiService } from '../../services/fastapiService';

export const InvoicesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const workspaceIdFromUrl = (searchParams.get('workspace_id') || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [resendLoadingId, setResendLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [workspaceIdFromUrl]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError('');
        const params: any = { page, limit };
        if (search) params.search = search;
        if (statusFilter) params.status = statusFilter;
        if (workspaceIdFromUrl) params.workspace_id = workspaceIdFromUrl;

        const res = await fastapiService.getInvoices(params);
        setInvoices(res.data || []);
        setTotal(res.pagination?.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [page, limit, search, statusFilter, workspaceIdFromUrl]);

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const openInvoicePdf = async (invoiceId: string) => {
    try {
      setPdfLoadingId(invoiceId);
      await fastapiService.regenerateAdminInvoicePdf(invoiceId);
      await fastapiService.openAdminInvoicePdfInNewTab(invoiceId);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to open invoice PDF');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const resendInvoiceEmail = async (invoiceId: string, customerEmail?: string) => {
    const ok = window.confirm(
      customerEmail
        ? `Resend invoice email with PDF to ${customerEmail}?`
        : 'Resend invoice email with PDF to the customer on file?',
    );
    if (!ok) return;
    try {
      setResendLoadingId(invoiceId);
      setError('');
      const res = await fastapiService.resendAdminInvoiceEmail(invoiceId);
      if (!(res as any)?.success && (res as any)?.error) {
        setError(String((res as any).error));
        return;
      }
      const msg = (res as any)?.data?.message || 'Invoice email sent.';
      window.alert(msg);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to resend invoice email');
    } finally {
      setResendLoadingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-admin-100 text-admin-700';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Invoices</h1>
        <p className="mt-1 text-sm text-admin-600">
          View and manage all invoices across the system
        </p>
        {workspaceIdFromUrl && (
          <p className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Filtered by workspace ID{' '}
            <span className="font-mono text-xs">{workspaceIdFromUrl}</span> — clear the URL query or open Invoices from the main menu to see all.
          </p>
        )}
      </div>
      
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4 gap-4">
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search by invoice number or email..."
            className="admin-input flex-1 max-w-md"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
            className="admin-input w-48"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-600">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Workspace</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {loading ? (
                <tr><td className="px-4 py-4 text-admin-500" colSpan={7}>Loading...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td className="px-4 py-4 text-admin-500" colSpan={7}>No invoices found</td></tr>
              ) : (
                invoices.map((inv: any) => (
                  <tr key={inv.id} className="hover:bg-admin-50">
                    <td className="px-4 py-4 text-sm font-mono text-admin-900">
                      {inv.invoice_number || `INV-${inv.id.substring(0, 8).toUpperCase()}`}
                    </td>
                    <td className="px-4 py-4 text-sm text-admin-900">
                      <div>{inv.user_profiles?.email || '-'}</div>
                      {inv.user_profiles?.full_name && (
                        <div className="text-xs text-admin-500">{inv.user_profiles.full_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-admin-700">
                      {inv.workspaces?.name || '-'}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-admin-900">
                      {formatCurrency(parseFloat(inv.amount || inv.total_amount || 0), inv.currency || 'INR')}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status)}`}>
                        {inv.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-admin-500">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button
                          type="button"
                          className="text-sky-700 hover:text-sky-900 font-medium disabled:opacity-50"
                          disabled={pdfLoadingId === inv.id}
                          onClick={() => openInvoicePdf(inv.id)}
                        >
                          {pdfLoadingId === inv.id ? 'Opening…' : 'View PDF'}
                        </button>
                        <button
                          type="button"
                          className="text-admin-700 hover:text-admin-900 font-medium disabled:opacity-50"
                          disabled={resendLoadingId === inv.id}
                          onClick={() => resendInvoiceEmail(inv.id, inv.user_profiles?.email)}
                        >
                          {resendLoadingId === inv.id ? 'Sending…' : 'Resend email'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-admin-600">
            Page {page} of {Math.ceil(total / limit)} · {total} total invoices
          </div>
          <div className="flex items-center gap-2">
            <button 
              className="admin-button" 
              disabled={page <= 1} 
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button 
              className="admin-button" 
              disabled={page >= Math.ceil(total / limit)} 
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

